import { useEffect, useMemo, useState } from 'react';
import PageLayout from '../components/layout/PageLayout';
import Seo from '../components/Seo';
import './FAQPage.css';

type FaqItem = {
  q: string;
  a: string;
  category: string;
};

const FAQ: FaqItem[] = [
  {
    category: 'Общее',
    q: 'Что такое Recvisit?',
    a: 'Recvisit — SaaS‑сервис учета посещаемости на объектах через QR‑коды: фиксирует события, формирует отчеты и помогает контролировать посещаемость в режиме реального времени.',
  },
  {
    category: 'Общее',
    q: 'Как начать работу?',
    a: 'Нажмите «Попробовать бесплатно», зарегистрируйтесь на reg.recvi.ru, затем войдите в панель управления и добавьте объекты и сотрудников.',
  },
  {
    category: 'Устройства',
    q: 'Какие устройства поддерживаются?',
    a: 'Сервис работает на широком спектре устройств. Актуальные минимальные требования размещены на странице «Системные требования».',
  },
  {
    category: 'Устройства',
    q: 'Нужно ли покупать специальный сканер?',
    a: 'Нет. Обычно достаточно подходящего планшета или смартфона, соответствующего минимальным требованиям.',
  },
  {
    category: 'Безопасность',
    q: 'Как защищены данные?',
    a: 'Мы используем защищенные каналы связи (HTTPS/TLS), контроль доступа и другие организационные/технические меры. Подробности — в Политике конфиденциальности.',
  },
  {
    category: 'Безопасность',
    q: 'Кто отвечает за персональные данные сотрудников?',
    a: 'Клиент (работодатель) выступает оператором персональных данных сотрудников. Сервис обрабатывает такие данные по поручению Клиента.',
  },
  {
    category: 'Тарифы',
    q: 'Сколько стоит Recvisit?',
    a: 'Актуальная стоимость и тарифы размещены на странице регистрации/в интерфейсе сервиса.',
  },
  {
    category: 'Поддержка',
    q: 'Как связаться с поддержкой?',
    a: 'Напишите на info@recvi.ru или используйте форму на странице «Контакты».',
  },
];

export default function FAQPage() {
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ;
    return FAQ.filter((i) => (i.q + ' ' + i.a + ' ' + i.category).toLowerCase().includes(q));
  }, [query]);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return Array.from(set);
  }, [items]);

  useEffect(() => {
    // FAQPage structured data
    const baseUrl = 'https://recvi.ru';
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((i) => ({
        '@type': 'Question',
        name: i.q,
        acceptedAnswer: { '@type': 'Answer', text: i.a },
      })),
    };

    const id = 'jsonld-faq';
    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      document.head.appendChild(script);
    }
    // Include base URL in a stable way (not required, but kept for consistency)
    void baseUrl;
    script.textContent = JSON.stringify(jsonLd);
  }, [items]);

  return (
    <PageLayout title="FAQ — Часто задаваемые вопросы">
      <Seo
        title="FAQ — Recvisit"
        description="Ответы на частые вопросы о Recvisit: требования, безопасность, оплата, поддержка."
        canonicalPath="/faq"
      />
      <div className="container">
        <div className="faq-search">
          <input
            className="faq-input"
            type="search"
            placeholder="Поиск по вопросам…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {categories.map((cat) => (
          <section key={cat} className="faq-section">
            <h2 className="faq-h2">{cat}</h2>
            <div className="faq-list">
              {items
                .filter((i) => i.category === cat)
                .map((i) => (
                  <details key={i.q} className="faq-item">
                    <summary className="faq-q">{i.q}</summary>
                    <div className="faq-a">{i.a}</div>
                  </details>
                ))}
            </div>
          </section>
        ))}

        {items.length === 0 && (
          <div className="faq-empty">
            Ничего не найдено. Попробуйте другой запрос.
          </div>
        )}
      </div>
    </PageLayout>
  );
}

